import { useEffect, useMemo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Breadcrumbs,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportUniqueUsers,
  fetchUniqueUserChart,
  fetchUniqueUsers
} from '../store/slices/uniqueUsersSlice';

const columns = [
  { id: 'date', label: 'Date', sortable: true },
  { id: 'daily', label: 'Daily', align: 'right', sortable: true },
  { id: 'weekly', label: 'Weekly', align: 'right', sortable: true },
  { id: 'monthly', label: 'Monthly', align: 'right', sortable: true }
];

const formatDisplayDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-GB');
};

const lineColors = {
  daily: '#0f766e',
  weekly: '#2563eb',
  monthly: '#ea580c'
};

const buildLinePoints = (items, key, width, height, minValue, maxValue) => {
  if (!items.length) return '';
  const chartWidth = width - 56;
  const chartHeight = height - 48;
  const denominator = Math.max(maxValue - minValue, 1);

  return items
    .map((item, index) => {
      const x = 32 + (items.length === 1 ? chartWidth / 2 : (index / (items.length - 1)) * chartWidth);
      const normalized = (item[key] - minValue) / denominator;
      const y = 12 + chartHeight - normalized * chartHeight;
      return `${x},${y}`;
    })
    .join(' ');
};

const UniqueUserLineChart = ({ items }) => {
  const chartItems = [...items].reverse();
  const width = 960;
  const height = 300;
  const values = chartItems.flatMap((item) => [item.daily, item.weekly, item.monthly]);
  const maxValue = values.length ? Math.max(...values) : 0;
  const minValue = 0;
  const lineKeys = ['daily', 'weekly', 'monthly'];
  const yTicks = 4;

  if (!chartItems.length) {
    return (
      <Box
        sx={{
          border: '1px dashed rgba(148, 163, 184, 0.5)',
          borderRadius: 1,
          py: 6,
          px: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          No chart data available
        </Typography>
        <Typography color="text.secondary">
          Adjust the date range to render unique user trends.
        </Typography>
      </Box>
    );
  }

  const xLabelStep = Math.max(1, Math.ceil(chartItems.length / 6));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: lineColors.daily }} />
          <Typography variant="body2" color="text.secondary">
            Daily
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: lineColors.weekly }} />
          <Typography variant="body2" color="text.secondary">
            Weekly
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: lineColors.monthly }} />
          <Typography variant="body2" color="text.secondary">
            Monthly
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 720 }}>
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Unique user trends">
            {Array.from({ length: yTicks + 1 }).map((_, index) => {
              const y = 12 + ((height - 48) / yTicks) * index;
              const value = Math.round(maxValue - ((maxValue - minValue) / yTicks) * index);

              return (
                <g key={`y-${index}`}>
                  <line x1="32" y1={y} x2={width - 24} y2={y} stroke="rgba(148, 163, 184, 0.24)" />
                  <text x="0" y={y + 4} fontSize="11" fill="#64748b">
                    {value.toLocaleString()}
                  </text>
                </g>
              );
            })}

            <line x1="32" y1={height - 36} x2={width - 24} y2={height - 36} stroke="rgba(148, 163, 184, 0.4)" />

            {lineKeys.map((key) => (
              <polyline
                key={key}
                fill="none"
                stroke={lineColors[key]}
                strokeWidth="3"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={buildLinePoints(chartItems, key, width, height, minValue, maxValue)}
              />
            ))}

            {chartItems.map((item, index) => {
              if (index % xLabelStep !== 0 && index !== chartItems.length - 1) {
                return null;
              }

              const chartWidth = width - 56;
              const x =
                32 + (chartItems.length === 1 ? chartWidth / 2 : (index / (chartItems.length - 1)) * chartWidth);

              return (
                <text
                  key={`x-${item.date}`}
                  x={x}
                  y={height - 12}
                  fontSize="11"
                  textAnchor="middle"
                  fill="#64748b"
                >
                  {formatDisplayDate(item.date)}
                </text>
              );
            })}
          </svg>
        </Box>
      </Box>
    </Stack>
  );
};

const UniqueUserPage = () => {
  const dispatch = useDispatch();
  const { items, chartItems, meta, loading, chartLoading, error, chartError, query, exportStatus, exportError } =
    useSelector((state) => state.uniqueUsers);

  const [startDate, setStartDate] = useState(query.startDate);
  const [endDate, setEndDate] = useState(query.endDate);
  const [page, setPage] = useState(Math.max((query.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query.pageSize ?? 30);
  const [sortBy, setSortBy] = useState(query.sortBy);
  const [sortDirection, setSortDirection] = useState(query.sortDirection);
  const [dense, setDense] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isDateRangeInvalid = startDate && endDate && new Date(startDate) > new Date(endDate);

  useEffect(() => {
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      fetchUniqueUsers({
        page: page + 1,
        pageSize: rowsPerPage,
        startDate,
        endDate,
        sortBy,
        sortDirection
      })
    );
  }, [dispatch, page, rowsPerPage, startDate, endDate, sortBy, sortDirection, isDateRangeInvalid]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      fetchUniqueUserChart({
        startDate,
        endDate,
        sortBy: 'date',
        sortDirection: 'desc'
      })
    );
  }, [dispatch, startDate, endDate, isDateRangeInvalid]);

  useEffect(() => {
    if (typeof query.page === 'number') {
      const zeroBasedPage = Math.max(query.page - 1, 0);
      setPage((prev) => (prev === zeroBasedPage ? prev : zeroBasedPage));
    }
    if (typeof query.pageSize === 'number') {
      setRowsPerPage((prev) => (prev === query.pageSize ? prev : query.pageSize));
    }
  }, [query.page, query.pageSize]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleExport = () => {
    setMenuAnchor(null);
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      exportUniqueUsers({
        startDate,
        endDate,
        sortBy,
        sortDirection
      })
    );
  };

  const sortedChartItems = useMemo(
    () => [...chartItems].sort((left, right) => right.date.localeCompare(left.date)),
    [chartItems]
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Unique User</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Unique User
          </Typography>
          <Typography color="text.secondary">
            Review unique-user trends for D-1, trailing 7 days, and trailing 30 days.
          </Typography>
        </Box>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 6,
            sx: {
              boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background:
                'radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff',
              borderRadius: 1,
              mt: 1
            }
          }}
        >
          <MenuItem onClick={handleExport} disabled={exportStatus === 'loading'}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
          </MenuItem>
        </Menu>
      </Stack>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ lg: 'center' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}
            >
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(event) => {
                  setPage(0);
                  setStartDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(event) => {
                  setPage(0);
                  setEndDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
            </Stack>
          </Stack>

          {isDateRangeInvalid && (
            <Alert severity="error">End date must be on or after start date.</Alert>
          )}
          {error && !loading && <Alert severity="error">{error}</Alert>}
          {chartError && !chartLoading && <Alert severity="error">{chartError}</Alert>}
          {exportError && <Alert severity="error">{exportError}</Alert>}

          {loading && <LinearProgress />}

          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 3 }, borderColor: 'rgba(148, 163, 184, 0.22)' }}
          >
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Unique User Trend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare D-1, trailing 7 days, and trailing 30 days across the selected range.
              </Typography>
            </Stack>
            <Box sx={{ mt: 3 }}>
              {chartLoading && <LinearProgress sx={{ mb: 2 }} />}
              <UniqueUserLineChart items={sortedChartItems} />
            </Box>
          </Paper>

          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <TableContainer>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sortDirection={sortBy === column.id ? sortDirection : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(15, 23, 42, 0.03)'
                        }
                      }}
                    >
                      <TableCell>{formatDisplayDate(row.date)}</TableCell>
                      <TableCell align="right">{row.daily.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.weekly.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.monthly.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {!items.length && !loading && !isDateRangeInvalid && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            No unique user reports found
                          </Typography>
                          <Typography color="text.secondary">
                            Adjust the date range to view additional records.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <FormControlLabel
              control={<Switch checked={dense} onChange={(event) => setDense(event.target.checked)} />}
              label="Dense"
            />

            <TablePagination
              component="div"
              count={meta?.total_items ?? 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 30, 50]}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                const nextRows = parseInt(event.target.value, 10);
                setRowsPerPage(nextRows);
                setPage(0);
              }}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 0, sm: 2 }
                }
              }}
            />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default UniqueUserPage;
